import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    ActionButton,
    Divider,
    Flex,
    Item,
    Menu,
    MenuTrigger,
    Picker,
    Section,
    Text,
} from '@adobe/react-spectrum';
import { CardTypeIcon } from '../../common/cardStats/CardTypeIcon';
import { CardStatList } from '../../common/cardStats/CardStatList';
import { StatMetricTile } from '../../common/cardStats/StatMetricTile';
import { StatsSection } from '../../common/cardStats/StatsSection';
import { formatInt, formatNumber, formatPct } from '../../common/cardStats/formatters';
import { renderManaText } from '../../common/cardStats/manaText';
import '../../filterpanel/mana-cost.css';

const CATEGORY_ORDER = ['triggers', 'abilities', 'statics', 'replacements'];

/**
 * Defensive filters for "top synergy anchors" — these cards anchor
 * high-scoring entries but aren't actually interesting synergy signal
 * (they're just staples). Lowercase everything for matching; category
 * casing isn't consistent in the raw data (e.g. "boardWipes" vs
 * "boardwipes"), so we lowercase the incoming tags at compare time too.
 */
const EXCLUDED_ANCHOR_CARD_IDS = new Set(['chrome_mox', 'mox_diamond']);
const EXCLUDED_ANCHOR_CATEGORIES = new Set(['counterspell', 'spotremoval', 'boardwipes']);

const CATEGORY_LABELS = {
    triggers: 'Triggered Abilities',
    abilities: 'Activated Abilities',
    statics: 'Static Abilities',
    replacements: 'Replacement Effects',
};

/** Short labels used in stacked bars / pills where space is limited. */
const CATEGORY_SHORT_LABELS = {
    triggers: 'Triggers',
    abilities: 'Abilities',
    statics: 'Statics',
    replacements: 'Replacements',
    enablers: 'Enablers',
    commanderBonus: 'Commander',
};

/** Color mapping shared by the entry-density bar, composition bar, and pills. */
const CATEGORY_COLORS = {
    triggers: '#cc1a1a', // red — active / dynamic
    abilities: '#e8c600', // gold — activated / payoff
    statics: '#4a9bd6', // blue — passive / persistent
    replacements: '#5fa85a', // green — modifies outcomes
    enablers: '#b87be8', // violet — support
    commanderBonus: '#ff6ea8', // pink — commander-tied bonus
};

const DEFAULT_CATEGORY_COLOR = '#8b93a7';

const SCORE_BREAKDOWN_ORDER = [
    'triggers',
    'statics',
    'replacements',
    'abilities',
    'enablers',
    'commanderBonus',
];

const GROUP_LABELS = {
    supporters: 'Supporting cards',
    enablers: 'Enablers',
};

const prettyFormatEffectText = (text) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
};


const categoryColor = (key) => CATEGORY_COLORS[key] || DEFAULT_CATEGORY_COLOR;

const fallbackCommanderImageUri = (commander) => {
    if (!commander) return '';
    if (commander.metadata?.image_uris?.border_crop) {
        return commander.metadata.image_uris.border_crop;
    }
    if (commander.metadata?.card_faces?.[0]?.image_uris?.border_crop) {
        return commander.metadata.card_faces[0].image_uris.border_crop;
    }
    const frontFace = commander.faces?.[commander.frontFaceId];
    return frontFace?.metadata?.image_uris?.border_crop || '';
};

const StatTile = (props) => <StatMetricTile {...props} />;

export function SynergyTab({ bin, onSelectCard }) {
    const commanders = useSelector((state) => state.commanderDetails?.details?.commanders) || [];

    const synergy = bin?.synergy || {};
    const synergyStats = bin?.synergyStats;

    const { effectMenu, commanderTabs } = useMemo(() => {
        const commanderMap = new Map();
        CATEGORY_ORDER.forEach((cat) => {
            const entries = synergy?.[cat];
            if (!entries) return;
            Object.keys(entries).forEach((entryKey) => {
                const node = entries[entryKey];
                const cid = node?.commanderCardId;
                if (!cid || commanderMap.has(cid)) return;
                commanderMap.set(cid, {
                    commanderCardId: cid,
                    commanderName: node?.commanderName,
                    commanderImageUri: node?.commanderImageUri,
                });
            });
        });
        const tabs = Array.from(commanderMap.values());

        const menu = CATEGORY_ORDER.filter((cat) => synergy?.[cat] && Object.keys(synergy[cat]).length > 0).map(
            (cat) => {
                const entries = synergy[cat];
                const children = Object.keys(entries).map((entryKey) => {
                    const node = entries[entryKey];
                    const effectText = prettyFormatEffectText(node?.text);
                    return {
                        id: `${cat}::${entryKey}`,
                        category: cat,
                        entryKey,
                        commanderCardId: node?.commanderCardId,
                        commanderName: node?.commanderName,
                        commanderImageUri: node?.commanderImageUri,
                        name: effectText,
                    };
                });
                return {
                    id: cat,
                    name: CATEGORY_LABELS[cat] || cat,
                    children,
                };
            }
        );

        return { effectMenu: menu, commanderTabs: tabs };
    }, [synergy]);

    const hasMultipleCommanders = commanderTabs.length > 1;
    const [selectedCommanderId, setSelectedCommanderId] = useState(
        hasMultipleCommanders ? commanderTabs[0]?.commanderCardId : null
    );

    // If the bin swaps (bracket change) the tabs list may differ — reset.
    useEffect(() => {
        if (!hasMultipleCommanders) {
            setSelectedCommanderId(null);
            return;
        }
        if (!commanderTabs.find((c) => c.commanderCardId === selectedCommanderId)) {
            setSelectedCommanderId(commanderTabs[0]?.commanderCardId || null);
        }
    }, [commanderTabs, hasMultipleCommanders, selectedCommanderId]);

    const filteredEffectMenu = useMemo(() => {
        if (!selectedCommanderId) return effectMenu;
        return effectMenu
            .map((section) => ({
                ...section,
                children: section.children.filter((c) => c.commanderCardId === selectedCommanderId),
            }))
            .filter((section) => section.children.length > 0);
    }, [effectMenu, selectedCommanderId]);

    const flatEntries = useMemo(
        () => filteredEffectMenu.flatMap((section) => section.children),
        [filteredEffectMenu]
    );
    const [selectedEffectId, setSelectedEffectId] = useState(flatEntries[0]?.id || null);

    useEffect(() => {
        if (!flatEntries.length) {
            setSelectedEffectId(null);
            return;
        }
        if (!selectedEffectId || !flatEntries.find((e) => e.id === selectedEffectId)) {
            setSelectedEffectId(flatEntries[0].id);
        }
    }, [flatEntries, selectedEffectId]);

    const selected = flatEntries.find((e) => e.id === selectedEffectId) || flatEntries[0];
    const entry = selected ? synergy?.[selected.category]?.[selected.entryKey] : null;
    const supporters = entry?.cardsOfSupportingType || [];
    const enablers = entry?.enablers || [];

    const groupOptions = useMemo(() => {
        const opts = [];
        if (supporters.length > 0) {
            opts.push({ key: 'supporters', label: `${GROUP_LABELS.supporters} (${supporters.length})` });
        }
        if (enablers.length > 0) {
            opts.push({ key: 'enablers', label: `${GROUP_LABELS.enablers} (${enablers.length})` });
        }
        return opts;
    }, [supporters, enablers]);

    const [selectedGroup, setSelectedGroup] = useState(groupOptions[0]?.key || 'supporters');

    useEffect(() => {
        if (!groupOptions.length) return;
        if (!groupOptions.find((g) => g.key === selectedGroup)) {
            setSelectedGroup(groupOptions[0].key);
        }
    }, [groupOptions, selectedGroup]);

    const activeCommanderTab = commanderTabs.find(
        (c) => c.commanderCardId === selectedCommanderId
    );
    const commanderImage =
        entry?.commanderImageUri ||
        selected?.commanderImageUri ||
        activeCommanderTab?.commanderImageUri ||
        fallbackCommanderImageUri(commanders[0]);
    const commanderName =
        entry?.commanderName ||
        selected?.commanderName ||
        activeCommanderTab?.commanderName ||
        commanders[0]?.name ||
        'Commander';

    const currentCards = selectedGroup === 'enablers' ? enablers : supporters;

    // ===== synergyStats-derived data =====
    const entryCategoryData = useMemo(() => {
        const source = synergyStats?.avgEntriesByCategory || {};
        const total = CATEGORY_ORDER.reduce((sum, key) => sum + (source[key] || 0), 0);
        if (total <= 0) return [];
        return CATEGORY_ORDER.filter((key) => (source[key] || 0) > 0).map((key) => ({
            key,
            label: CATEGORY_SHORT_LABELS[key] || key,
            color: categoryColor(key),
            avg: source[key] || 0,
            share: (source[key] || 0) / total,
        }));
    }, [synergyStats]);

    const scoreBreakdownData = useMemo(() => {
        const source = synergyStats?.scoreBreakdownShare || {};
        return SCORE_BREAKDOWN_ORDER.filter((key) => (source[key] || 0) > 0).map((key) => ({
            key,
            label: CATEGORY_SHORT_LABELS[key] || key,
            color: categoryColor(key),
            share: source[key] || 0,
        }));
    }, [synergyStats]);

    const topAnchors = useMemo(() => {
        const raw = synergyStats?.topAnchorCards || [];
        return raw.filter((anchor) => {
            if (EXCLUDED_ANCHOR_CARD_IDS.has(anchor?.cardId)) return false;
            const cats = (anchor?.categories || []).map((c) =>
                (c || '').toLowerCase()
            );
            return !cats.some((c) => EXCLUDED_ANCHOR_CATEGORIES.has(c));
        });
    }, [synergyStats]);
    const topSynergies = synergyStats?.topSynergies || [];

    const hasAnyData = flatEntries.length > 0 || synergyStats;

    if (!hasAnyData) {
        return <Text UNSAFE_className="commanderCardStatsEmpty">No synergy data for this bucket.</Text>;
    }

    const tribalTheme = (synergyStats?.avgGenericTypeMatchEntries || 0) > 3;
    const commanderTheme = (synergyStats?.avgCommanderBonusEntries || 0) > 2;

    return (
        <Flex direction="column" rowGap={24} marginTop={10}>
            {/* ----- DECK-WIDE STATS ----- */}
            {synergyStats && (
                <>
                    <div className="statsMetricsGrid">
                        <StatTile
                            label="Synergy score"
                            value={formatInt(synergyStats.avgDeckScore)}
                            hint={`max ${formatInt(synergyStats.maxDeckScore)}`}
                        />
                        <StatTile
                            label="Entries / deck"
                            value={formatNumber(synergyStats.avgEntryCount, 1)}
                            hint={`avg ${formatNumber(synergyStats.avgSupportersPerEntry, 1)} sup · ${formatNumber(
                                synergyStats.avgEnablersPerEntry,
                                1
                            )} ena per entry`}
                        />
                        <StatTile
                            label="Avg entry score"
                            value={formatNumber(synergyStats.avgTotalEntryScore, 1)}
                            hint="mean score across every entry"
                        />
                        <StatTile
                            label="Headline entry"
                            value={formatInt(synergyStats.avgMaxEntryScore)}
                            hint="biggest single entry / deck"
                        />
                    </div>

                    {entryCategoryData.length > 0 && (
                        <StatsSection
                            title="Entry density by category"
                            subtitle="Where synergy entries live — triggered vs activated vs static vs replacement"
                        >
                            <div className="synergyStackedBar">
                                {entryCategoryData.map((segment) => (
                                    <div
                                        key={segment.key}
                                        className="synergyStackedSegment"
                                        style={{
                                            width: `${Math.max(segment.share * 100, 2)}%`,
                                            background: segment.color,
                                        }}
                                        title={`${segment.label} — avg ${formatNumber(
                                            segment.avg,
                                            1
                                        )} entries / deck`}
                                    />
                                ))}
                            </div>
                            <div className="synergyStackedLegend">
                                {entryCategoryData.map((segment) => (
                                    <div key={segment.key} className="synergyStackedLegendItem">
                                        <span
                                            className="synergyStackedSwatch"
                                            style={{ background: segment.color }}
                                        />
                                        <span className="synergyStackedLegendLabel">
                                            {segment.label}
                                        </span>
                                        <span className="synergyStackedLegendValue">
                                            {formatNumber(segment.avg, 1)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </StatsSection>
                    )}

                    {scoreBreakdownData.length > 0 && (
                        <StatsSection
                            title="Score composition"
                            subtitle="Where synergy score comes from — condition layers + enablers + commander bonus"
                        >
                            <div className="synergyStackedBar">
                                {scoreBreakdownData.map((segment) => (
                                    <div
                                        key={segment.key}
                                        className="synergyStackedSegment"
                                        style={{
                                            width: `${Math.max(segment.share * 100, 2)}%`,
                                            background: segment.color,
                                        }}
                                        title={`${segment.label} — ${formatPct(segment.share)}`}
                                    />
                                ))}
                            </div>
                            <div className="synergyStackedLegend">
                                {scoreBreakdownData.map((segment) => (
                                    <div key={segment.key} className="synergyStackedLegendItem">
                                        <span
                                            className="synergyStackedSwatch"
                                            style={{ background: segment.color }}
                                        />
                                        <span className="synergyStackedLegendLabel">
                                            {segment.label}
                                        </span>
                                        <span className="synergyStackedLegendValue">
                                            {formatPct(segment.share)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </StatsSection>
                    )}
                </>
            )}

            {/* ----- COMMANDER SYNERGY DRILL-DOWN ----- */}
            {flatEntries.length > 0 && (
                <StatsSection
                    title="Commander synergies"
                    subtitle={
                        hasMultipleCommanders
                            ? 'Pick a partner to drill into their anchored effects'
                            : 'Detailed supporter + enabler lists for commander-anchored effects'
                    }
                >
                    {hasMultipleCommanders && (
                        <div className="synergyCommanderTabs">
                            {commanderTabs.map((cmd) => {
                                const isActive = cmd.commanderCardId === selectedCommanderId;
                                return (
                                    <button
                                        key={cmd.commanderCardId}
                                        type="button"
                                        className={`synergyCommanderTab ${
                                            isActive ? 'is-active' : ''
                                        }`}
                                        onClick={() => setSelectedCommanderId(cmd.commanderCardId)}
                                        title={`Show ${cmd.commanderName}'s synergy entries`}
                                    >
                                        {cmd.commanderImageUri && (
                                            <img
                                                src={cmd.commanderImageUri}
                                                alt={cmd.commanderName}
                                                className="synergyCommanderTabThumb"
                                                loading="lazy"
                                            />
                                        )}
                                        <span className="synergyCommanderTabName">
                                            {cmd.commanderName}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    <Flex
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                        UNSAFE_className="synergyHeaderBlock"
                        rowGap={10}
                    >
                        {commanderImage && (
                            <img
                                src={commanderImage}
                                alt={commanderName}
                                className="synergyCommanderImage"
                            />
                        )}
                        <Flex direction="row" alignItems="center" columnGap={10} wrap rowGap={10}>
                            <Text UNSAFE_className="synergyMenuBoxLabel">EFFECT:</Text>
                            <MenuTrigger>
                                <ActionButton UNSAFE_className="synergyEffectButton">
                                    {renderManaText(
                                        prettyFormatEffectText(entry?.text) || selected?.name || 'Select'
                                    )}
                                </ActionButton>
                                <Menu
                                    UNSAFE_className="synergyMenuBox"
                                    items={filteredEffectMenu}
                                    selectedKeys={selectedEffectId ? [selectedEffectId] : []}
                                    onSelectionChange={(keys) => {
                                        const next = Array.from(keys)[0];
                                        if (next) setSelectedEffectId(next);
                                    }}
                                    selectionMode="single"
                                >
                                    {(section) => (
                                        <Section items={section.children} title={section.name}>
                                            {(item) => (
                                                <Item key={item.id} textValue={item.name}>
                                                    <Text>{renderManaText(item.name)}</Text>
                                                </Item>
                                            )}
                                        </Section>
                                    )}
                                </Menu>
                            </MenuTrigger>
                        </Flex>
                    </Flex>

                    <Divider size="S" />

                    {groupOptions.length === 0 ? (
                        <Text UNSAFE_className="commanderCardStatsEmpty">
                            No supporting cards recorded for this effect in this bucket.
                        </Text>
                    ) : (
                        <Flex direction="column" rowGap={10}>
                            {groupOptions.length < 2 ? (
                                <Text UNSAFE_className="synergyCardListTitle">
                                    {groupOptions[0]?.label}
                                </Text>
                            ) : (
                                <Flex direction="row" alignItems="end">
                                    <Picker
                                        aria-label="Card group"
                                        selectedKey={selectedGroup}
                                        onSelectionChange={setSelectedGroup}
                                        items={groupOptions}
                                        width={260}
                                    >
                                        {(option) => <Item key={option.key}>{option.label}</Item>}
                                    </Picker>
                                </Flex>
                            )}
                            <div className="synergyCardListBlock synergyCardListScroll">
                                <CardStatList
                                    cards={currentCards}
                                    deckCount={bin.deckCount}
                                    onSelectCard={onSelectCard}
                                    resetKey={`synergy:${selectedEffectId}:${selectedGroup}:${selectedCommanderId || 'all'}`}
                                    initialVisible={60}
                                    compactFilters
                                    emptyMessageUnfiltered="No cards recorded for this effect in this bucket."
                                    renderCard={(card) => (
                                        <SynergyCardRow
                                            key={card.id || card.cardId}
                                            card={card}
                                            deckCount={bin.deckCount}
                                            onSelectCard={onSelectCard}
                                        />
                                    )}
                                />
                            </div>
                        </Flex>
                    )}
                </StatsSection>
            )}

            {synergyStats && (
                <>
                    {(tribalTheme || commanderTheme) && (
                        <Flex direction="row" columnGap={8} wrap rowGap={6}>
                            {tribalTheme && (
                                <span
                                    className="synergyThemeBadge"
                                    style={{
                                        borderLeftColor: '#e8c600',
                                        color: '#e8c600',
                                    }}
                                    title={`Avg ${formatNumber(
                                        synergyStats.avgGenericTypeMatchEntries,
                                        1
                                    )} entries per deck flagged as generic type-match`}
                                >
                                    Tribal theme
                                </span>
                            )}
                            {commanderTheme && (
                                <span
                                    className="synergyThemeBadge"
                                    style={{
                                        borderLeftColor: '#ff6ea8',
                                        color: '#ff6ea8',
                                    }}
                                    title={`Avg ${formatNumber(
                                        synergyStats.avgCommanderBonusEntries,
                                        1
                                    )} entries per deck carry a commander bonus`}
                                >
                                    Commander-synergy heavy
                                </span>
                            )}
                        </Flex>
                    )}

                    {topAnchors.length > 0 && (
                        <StatsSection
                            title="Top synergy anchors"
                            subtitle="Non-commander cards that consistently drive synergy in this commander's decks"
                        >
                            <CardStatList
                                cards={topAnchors}
                                deckCount={bin.deckCount}
                                onSelectCard={onSelectCard}
                                resetKey={`synergy-anchors:${bin?.binKey}`}
                                initialVisible={10}
                                expandStep={10}
                                compactFilters
                                renderCard={(anchor) => (
                                    <SynergyAnchorRow
                                        key={anchor.cardId}
                                        anchor={anchor}
                                        deckCount={bin.deckCount}
                                        onSelectCard={onSelectCard}
                                    />
                                )}
                            />
                        </StatsSection>
                    )}

                    {topSynergies.length > 0 && (
                        <StatsSection
                            title="Top synergies"
                            subtitle="Individual ability triggers ranked by amortized score across the bin"
                        >
                            <Flex direction="column" rowGap={6}>
                                {topSynergies.slice(0, 15).map((synergyItem) => (
                                    <TopSynergyRow
                                        key={synergyItem.key}
                                        synergy={synergyItem}
                                        deckCount={bin.deckCount}
                                        onSelectCard={onSelectCard}
                                    />
                                ))}
                            </Flex>
                        </StatsSection>
                    )}
                </>
            )}
        </Flex>
    );
}

function SynergyAnchorRow({ anchor, deckCount, onSelectCard }) {
    const name = anchor?.name || anchor?.cardId || 'Unknown';
    const imageUri = anchor?.imageUri;
    const cardId = anchor?.cardId || anchor?.id;
    const pct = Math.round((anchor?.deckRate || 0) * 100);

    const handleClick = () => {
        if (onSelectCard && cardId) onSelectCard({ id: cardId, cardId, name, imageUri });
    };

    return (
        <Flex
            direction="row"
            alignItems="center"
            columnGap={10}
            UNSAFE_className="synergyAnchorRow"
        >
            {imageUri ? (
                <img
                    src={imageUri}
                    alt={name}
                    className="cardStatThumb"
                    onClick={handleClick}
                    loading="lazy"
                />
            ) : (
                <div className="cardStatThumbPlaceholder" />
            )}
            <CardTypeIcon types={anchor?.types} />
            <span
                className="cardStatName"
                onClick={handleClick}
                style={{ cursor: imageUri ? 'pointer' : 'default' }}
                title={imageUri ? `Click to view ${name}` : name}
            >
                {name}
            </span>
            <div className="synergyAnchorScores">
                <span className="synergyAnchorScore" title="Avg synergy entries on this card per deck">
                    {`${formatNumber(anchor?.avgEntryCount, 1)} ent`}
                </span>
                <span
                    className="synergyAnchorScore is-primary"
                    title="Avg synergy score contribution per deck that had this card"
                >
                    {`${formatInt(anchor?.avgTotalScore)} pts`}
                </span>
            </div>
            <div className="cardStatBarTrack">
                <div className="cardStatBarFill" style={{ width: `${Math.max(pct, 1)}%` }} />
            </div>
            <Text UNSAFE_className="cardStatPercent">{`${pct}%`}</Text>
            <Text UNSAFE_className="cardStatCount">{`${anchor?.deckCount || 0}/${deckCount}`}</Text>
        </Flex>
    );
}

function TopSynergyRow({ synergy, deckCount, onSelectCard }) {
    const name = synergy?.anchorName || synergy?.anchorCardId || 'Unknown';
    const imageUri = synergy?.anchorImageUri;
    const cardId = synergy?.anchorCardId;
    const pct = Math.round((synergy?.deckRate || 0) * 100);
    const category = synergy?.category;
    const catColor = categoryColor(category);

    const handleClick = () => {
        if (onSelectCard && cardId) onSelectCard({ id: cardId, cardId, name, imageUri });
    };

    return (
        <div className="topSynergyRow">
            {imageUri ? (
                <img
                    src={imageUri}
                    alt={name}
                    className="cardStatThumb"
                    onClick={handleClick}
                    loading="lazy"
                />
            ) : (
                <div className="cardStatThumbPlaceholder" />
            )}
            <div className="topSynergyBody">
                <div className="topSynergyHeader">
                    <span
                        className="topSynergyCategory"
                        style={{ borderLeftColor: catColor, color: catColor }}
                    >
                        {CATEGORY_SHORT_LABELS[category] || category}
                    </span>
                    <span
                        className="topSynergyName"
                        onClick={handleClick}
                        style={{ cursor: imageUri ? 'pointer' : 'default' }}
                    >
                        {name}
                    </span>
                </div>
                {synergy?.text && (
                    <span className="topSynergyText">
                        &ldquo;{renderManaText(prettyFormatEffectText(synergy.text))}&rdquo;
                    </span>
                )}
            </div>
            <div className="topSynergyMeta">
                <span
                    className="topSynergyScore"
                    title="Amortized score across the entire bin (ranking metric)"
                >
                    {formatInt(synergy?.avgScoreAcrossAllDecks)}
                </span>
                <span className="topSynergyPct">{`${pct}%`}</span>
                <span className="topSynergyCount">{`${synergy?.deckCount || 0}/${deckCount}`}</span>
            </div>
        </div>
    );
}

function SynergyCardRow({ card, deckCount, onSelectCard }) {
    const name = card?.name || card?.id || card?.cardId || 'Unknown';
    const imageUri = card?.imageUri;
    const cardId = card?.cardId || card?.id;
    const count = card?.count || 0;
    const pct = Math.round((card?.inclusionRate || 0) * 100);

    const handleClick = () => {
        if (onSelectCard && cardId) onSelectCard({ id: cardId, cardId, name, imageUri, types: card?.types });
    };

    return (
        <Flex direction="row" alignItems="center" columnGap={10} UNSAFE_className="synergyCardRow">
            {imageUri ? (
                <img
                    src={imageUri}
                    alt={name}
                    className="cardStatThumb"
                    onClick={handleClick}
                    loading="lazy"
                />
            ) : (
                <div className="cardStatThumbPlaceholder" />
            )}
            <CardTypeIcon types={card?.types} />
            <Flex direction="column" flex={1} minWidth={0}>
                <span
                    className="synergyCardRowName"
                    onClick={handleClick}
                    style={{ cursor: imageUri ? 'pointer' : 'default' }}
                    title={imageUri ? `Click to view ${name}` : name}
                >
                    {name}
                </span>
                {card?.text && (
                    <span className="synergyCardRowText">
                        &ldquo;{renderManaText(card.text)}&rdquo;
                    </span>
                )}
            </Flex>
            <Text UNSAFE_className="cardStatPercent">{`${pct}%`}</Text>
            <Text UNSAFE_className="cardStatCount">{`${count}/${deckCount}`}</Text>
        </Flex>
    );
}
